import { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useDI } from "@/infrastructure/di/useDI";
import { useBillingViewModel } from "./BillingViewModel";
import {
  PageContent,
  SectionCard,
  PageHeader,
} from "@/adapters/inbound/layout";
import { Title } from "react-admin";

function formatDate(value: Date | null, locale = "de-DE"): string {
  if (!value) return "—";
  return new Intl.DateTimeFormat(locale, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(value);
}

function formatCurrencyFromCents(
  cents: number,
  currency: string,
  locale = "de-DE",
): string {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(cents / 100);
}

function formatPercentFromBps(bps: number, locale = "de-DE"): string {
  return new Intl.NumberFormat(locale, {
    style: "percent",
    maximumFractionDigits: 2,
  }).format(bps / 10_000);
}

function humanizeStatus(status: string): string {
  switch (status.toLowerCase()) {
    case "active":
      return "Aktiv";
    case "trialing":
      return "Testphase";
    case "past_due":
      return "Überfällig";
    case "canceled":
      return "Beendet";
    case "unpaid":
      return "Unbezahlt";
    case "incomplete":
      return "Unvollständig";
    case "incomplete_expired":
      return "Abgelaufen";
    default:
      return status;
  }
}

function humanizePendingType(type: string): string {
  switch (type.toLowerCase()) {
    case "seat_increase":
      return "Sitzerhöhung";
    case "seat_decrease":
      return "Sitzreduzierung";
    case "cancellation":
      return "Kündigung";
    case "reactivation":
      return "Reaktivierung";
    default:
      return type;
  }
}

function humanizePendingStatus(status: string): string {
  switch (status.toLowerCase()) {
    case "pending":
      return "Ausstehend";
    case "scheduled":
      return "Geplant";
    case "applied":
      return "Übernommen";
    case "canceled":
      return "Abgebrochen";
    default:
      return status;
  }
}

function LabelValue({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <Stack spacing={0.5}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body1" fontWeight={600}>
        {value}
      </Typography>
    </Stack>
  );
}

function calculateMonthlySeatPrice({
  seatQuantity,
  pricePerSeatNetCents,
  vatRateBps,
}: {
  seatQuantity: number;
  pricePerSeatNetCents: number;
  vatRateBps: number;
}) {
  const netCents = seatQuantity * pricePerSeatNetCents;
  const vatCents = Math.round((netCents * vatRateBps) / 10_000);

  return {
    netCents,
    vatCents,
    grossCents: netCents + vatCents,
  };
}

function seatWord(count: number): string {
  return count === 1 ? "Sitz" : "Sitze";
}

export default function BillingPage() {
  const di = useDI();

  const { state, derived, intents } = useBillingViewModel({
    getAccountBilling: di.getAccountBilling,
    increaseAccountSeats: di.increaseAccountSeats,
    decreaseAccountSeats: di.decreaseAccountSeats,
    cancelAccountBilling: di.cancelAccountBilling,
    reactivateAccountBilling: di.reactivateAccountBilling,
    getBillingPortalLink: di.getBillingPortalLink,
  });

  const [isSeatIncreaseDialogOpen, setSeatIncreaseDialogOpen] = useState(false);

  const pricing = useMemo(() => {
    const subscription = derived.subscription;
    if (!subscription) return null;

    return {
      net: formatCurrencyFromCents(
        subscription.pricePerSeatNetCents,
        subscription.currency,
      ),
      vat: formatPercentFromBps(subscription.vatRateBps),
    };
  }, [derived.subscription]);

  if (state.isInitialLoading) {
    return (
      <PageContent width="content">
        <Stack spacing={3} alignItems="center">
          <CircularProgress />
          <Typography color="text.secondary">
            Kontodaten werden geladen …
          </Typography>
        </Stack>
      </PageContent>
    );
  }

  if (state.loadError || !derived.subscription) {
    return (
      <PageContent width="content">
        <Stack spacing={3}>
          <PageHeader title="Konto" />

          <Alert severity="error">
            {state.loadError ?? "Kontodaten konnten nicht geladen werden."}
          </Alert>

          <Box>
            <Button
              variant="contained"
              onClick={() => void intents.reload()}
              disabled={state.isRefreshing}
            >
              {state.isRefreshing ? "Wird geladen …" : "Erneut laden"}
            </Button>
          </Box>
        </Stack>
      </PageContent>
    );
  }

  const subscription = derived.subscription;
  const pendingChange = derived.pendingChange;
  const isCancelling = subscription.cancelAtPeriodEnd;

  const targetSeatQuantity = Number.parseInt(state.targetSeatQuantityInput, 10);

  const hasValidTargetSeatQuantity =
    Number.isFinite(targetSeatQuantity) && targetSeatQuantity >= 1;

  const targetSeatQuantityError =
    state.targetSeatQuantityInput.trim().length === 0
      ? "Bitte geben Sie eine Sitzanzahl ein."
      : !hasValidTargetSeatQuantity
        ? "Bitte eine gültige Sitzanzahl größer oder gleich 1 eingeben."
        : targetSeatQuantity < subscription.usedSeatQuantity
          ? `Die Zielanzahl darf nicht unter den aktuell genutzten ${subscription.usedSeatQuantity} ${seatWord(
            subscription.usedSeatQuantity,
          )} liegen.`
          : null;

  const seatDelta = hasValidTargetSeatQuantity
    ? targetSeatQuantity - subscription.purchasedSeatQuantity
    : 0;

  const isSeatIncrease = seatDelta > 0;
  const isSeatDecrease = seatDelta < 0;
  const absoluteSeatDelta = Math.abs(seatDelta);

  const currentMonthlyPrice = calculateMonthlySeatPrice({
    seatQuantity: subscription.purchasedSeatQuantity,
    pricePerSeatNetCents: subscription.pricePerSeatNetCents,
    vatRateBps: subscription.vatRateBps,
  });

  const targetMonthlyPrice = hasValidTargetSeatQuantity
    ? calculateMonthlySeatPrice({
      seatQuantity: targetSeatQuantity,
      pricePerSeatNetCents: subscription.pricePerSeatNetCents,
      vatRateBps: subscription.vatRateBps,
    })
    : currentMonthlyPrice;

  const monthlyDelta = {
    netCents: targetMonthlyPrice.netCents - currentMonthlyPrice.netCents,
    vatCents: targetMonthlyPrice.vatCents - currentMonthlyPrice.vatCents,
    grossCents: targetMonthlyPrice.grossCents - currentMonthlyPrice.grossCents,
  };

  const absoluteMonthlyDelta = {
    netCents: Math.abs(monthlyDelta.netCents),
    vatCents: Math.abs(monthlyDelta.vatCents),
    grossCents: Math.abs(monthlyDelta.grossCents),
  };

  const canSubmitSeatQuantity =
    derived.canSubmitSeatQuantity &&
    targetSeatQuantityError === null &&
    seatDelta !== 0;

  const submitSeatQuantity = () => {
    if (!canSubmitSeatQuantity) return;

    if (isSeatIncrease) {
      setSeatIncreaseDialogOpen(true);
      return;
    }

    void intents.submitSeatQuantity();
  };

  const confirmSeatIncrease = () => {
    setSeatIncreaseDialogOpen(false);
    void intents.submitSeatQuantity();
  };



  return (
    <>
      <Title title="Konto & Abrechnung" />
      <PageContent width="content">
        <Stack spacing={3}>
          <PageHeader
            title="Konto & Abrechnung"
            subtitle="Verwalten Sie Ihr Abonnement, Sitzplätze und die Abrechnung."
          />

          {state.successMessage ? (
            <Alert severity="success" onClose={intents.clearMessages}>
              {state.successMessage}
            </Alert>
          ) : null}

          {state.actionError ? (
            <Alert severity="error" onClose={intents.clearMessages}>
              {state.actionError}
            </Alert>
          ) : null}

          <SectionCard
            title="Abonnement"
            subtitle="Aktueller Status und laufende Abrechnungsperiode."
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  label={humanizeStatus(subscription.status)}
                  color={
                    subscription.status.toLowerCase() === "active"
                      ? "success"
                      : "default"
                  }
                  variant="outlined"
                />
                {subscription.cancelAtPeriodEnd ? (
                  <Chip
                    label="Endet zum Periodenende"
                    color="warning"
                    variant="outlined"
                  />
                ) : null}
              </Stack>
            </Stack>

            <Divider />

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={3}
              useFlexGap
              flexWrap="wrap"
            >
              <LabelValue
                label="Gebuchte Sitze"
                value={subscription.purchasedSeatQuantity}
              />
              <LabelValue
                label="Aktiv genutzt"
                value={subscription.usedSeatQuantity}
              />
              <LabelValue label="Frei" value={subscription.freeSeatQuantity} />
              <LabelValue
                label="Aktuelle Periode ab"
                value={formatDate(subscription.currentPeriodStart)}
              />
              <LabelValue
                label="Aktuelle Periode bis"
                value={formatDate(subscription.currentPeriodEnd)}
              />
              <LabelValue
                label="Preis pro Sitz netto"
                value={pricing?.net ?? "—"}
              />
              <LabelValue label="Umsatzsteuer" value={pricing?.vat ?? "—"} />
            </Stack>
          </SectionCard>

          <SectionCard
            title="Sitzanzahl ändern"
            subtitle="Erhöhungen kaufen zusätzliche Sitze und erhöhen die monatlichen Kosten. Reduzierungen können geplant werden."
            actions={
              <Button
                variant="contained"
                onClick={submitSeatQuantity}
                disabled={!canSubmitSeatQuantity}
              >
                {derived.isSeatChangeBusy
                  ? "Wird gespeichert …"
                  : isSeatIncrease
                    ? `${absoluteSeatDelta} ${seatWord(absoluteSeatDelta)} kaufen`
                    : isSeatDecrease
                      ? "Reduzierung anfragen"
                      : "Sitzanzahl aktualisieren"}
              </Button>
            }
          >
            <Stack spacing={2}>
              <TextField
                label="Zielanzahl Sitze"
                type="number"
                value={state.targetSeatQuantityInput}
                onChange={(event) =>
                  intents.setTargetSeatQuantityInput(event.target.value)
                }
                disabled={derived.isBusy}
                error={targetSeatQuantityError !== null}
                helperText={
                  targetSeatQuantityError ??
                  "Geben Sie die gewünschte Gesamtanzahl ein, nicht die Anzahl zusätzlicher Sitze."
                }
                slotProps={{
                  htmlInput: {
                    min: Math.max(1, subscription.usedSeatQuantity),
                    step: 1,
                    inputMode: "numeric",
                  },
                }}
                fullWidth
              />

              {isSeatIncrease ? (
                <Alert severity="warning">
                  Sie erhöhen von{" "}
                  <strong>{subscription.purchasedSeatQuantity}</strong> auf{" "}
                  <strong>{targetSeatQuantity}</strong> {seatWord(targetSeatQuantity)}.
                  Dadurch kaufen Sie{" "}
                  <strong>
                    {absoluteSeatDelta} {seatWord(absoluteSeatDelta)}
                  </strong>{" "}
                  zusätzlich.
                  <br />
                  Neue monatliche Kosten:{" "}
                  <strong>
                    {formatCurrencyFromCents(
                      targetMonthlyPrice.netCents,
                      subscription.currency,
                    )}
                  </strong>{" "}
                  netto zzgl.{" "}
                  <strong>{formatPercentFromBps(subscription.vatRateBps)} USt.</strong>
                  {" "}(
                  <strong>
                    {formatCurrencyFromCents(
                      targetMonthlyPrice.grossCents,
                      subscription.currency,
                    )}
                  </strong>{" "}
                  inkl. USt.).
                </Alert>
              ) : isSeatDecrease ? (
                <Alert severity="info">
                  Sie reduzieren von{" "}
                  <strong>{subscription.purchasedSeatQuantity}</strong> auf{" "}
                  <strong>{targetSeatQuantity}</strong> {seatWord(targetSeatQuantity)}.
                  Dadurch werden{" "}
                  <strong>
                    {absoluteSeatDelta} {seatWord(absoluteSeatDelta)}
                  </strong>{" "}
                  weniger berechnet.
                  <br />
                  Monatliche Ersparnis nach Wirksamwerden:{" "}
                  <strong>
                    {formatCurrencyFromCents(
                      absoluteMonthlyDelta.netCents,
                      subscription.currency,
                    )}
                  </strong>{" "}
                  netto zzgl.{" "}
                  <strong>{formatPercentFromBps(subscription.vatRateBps)} USt.</strong>
                  {" "}(
                  <strong>
                    {formatCurrencyFromCents(
                      absoluteMonthlyDelta.grossCents,
                      subscription.currency,
                    )}
                  </strong>{" "}
                  inkl. USt.).
                  <br />
                  Neue monatliche Kosten:{" "}
                  <strong>
                    {formatCurrencyFromCents(
                      targetMonthlyPrice.netCents,
                      subscription.currency,
                    )}
                  </strong>{" "}
                  netto zzgl.{" "}
                  {formatCurrencyFromCents(
                    targetMonthlyPrice.vatCents,
                    subscription.currency,
                  )}{" "}
                  USt. ={" "}
                  <strong>
                    {formatCurrencyFromCents(
                      targetMonthlyPrice.grossCents,
                      subscription.currency,
                    )}
                  </strong>{" "}
                  inkl. USt.
                  <br />
                  Die Reduzierung wird angefragt und kann zum Periodenende wirksam werden.
                </Alert>
              ) : (
                <Alert severity="info">
                  Aktuell gebucht:{" "}
                  <strong>{subscription.purchasedSeatQuantity}</strong>, aktiv genutzt:{" "}
                  <strong>{subscription.usedSeatQuantity}</strong>, frei:{" "}
                  <strong>{subscription.freeSeatQuantity}</strong>.
                </Alert>
              )}
            </Stack>
          </SectionCard>

          {pendingChange ? (
            <SectionCard
              title="Ausstehende Änderung"
              subtitle="Noch nicht wirksame oder geplante Änderung."
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={3}
                useFlexGap
                flexWrap="wrap"
              >
                <LabelValue
                  label="Typ"
                  value={humanizePendingType(pendingChange.type)}
                />
                <LabelValue
                  label="Status"
                  value={humanizePendingStatus(pendingChange.status)}
                />
                <LabelValue
                  label="Angefragte Sitze"
                  value={pendingChange.requestedSeatQuantity ?? "—"}
                />
                <LabelValue
                  label="Aktive Sitze"
                  value={pendingChange.activeSeatQuantity ?? "—"}
                />
                <LabelValue
                  label="Geplante Sitze"
                  value={pendingChange.scheduledSeatQuantity ?? "—"}
                />
                <LabelValue
                  label="Wirksam ab"
                  value={formatDate(pendingChange.takesEffectAt)}
                />
                <LabelValue
                  label="Erstellt"
                  value={formatDate(pendingChange.createdAt)}
                />
                <LabelValue
                  label="Aktualisiert"
                  value={formatDate(pendingChange.updatedAt)}
                />
              </Stack>
            </SectionCard>
          ) : null}

          <SectionCard
            title="Kündigung"
            subtitle={
              isCancelling
                ? "Ihr Abonnement ist zur Beendigung vorgemerkt."
                : "Beenden Sie das Abonnement zum Ende der aktuellen Periode."
            }
            actions={
              isCancelling ? (
                <Button
                  variant="contained"
                  onClick={() => void intents.reactivate()}
                  disabled={derived.isBusy}
                >
                  {derived.isReactivateBusy
                    ? "Wird reaktiviert …"
                    : "Abonnement reaktivieren"}
                </Button>
              ) : (
                <Button
                  variant="outlined"
                  color="warning"
                  onClick={() => void intents.cancelAtPeriodEnd()}
                  disabled={derived.isBusy}
                >
                  {derived.isCancelBusy
                    ? "Wird vorgemerkt …"
                    : "Zum Periodenende kündigen"}
                </Button>
              )
            }
          >
            <Stack spacing={1.5}>
              {isCancelling ? (
                <>
                  <Typography variant="body1">
                    Das Abonnement endet zum Ende der laufenden Periode.
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Geplantes Ende: {formatDate(subscription.currentPeriodEnd)}
                  </Typography>
                </>
              ) : (
                <>
                  <Typography variant="body1">
                    Das Abonnement bleibt bis zum Periodenende aktiv.
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Ende der aktuellen Periode:{" "}
                    {formatDate(subscription.currentPeriodEnd)}
                  </Typography>
                </>
              )}
            </Stack>
          </SectionCard>

          <SectionCard
            title="Billing Portal"
            subtitle="Öffnen Sie das externe Portal für Rechnungen und Zahlungsdaten."
            actions={
              <Button
                variant="outlined"
                endIcon={<OpenInNewIcon />}
                onClick={() => void intents.openBillingPortal()}
                disabled={derived.isBusy}
              >
                {derived.isPortalBusy
                  ? "Wird geöffnet …"
                  : "Billing Portal öffnen"}
              </Button>
            }
          >
            <Typography variant="body2" color="text.secondary">
              Dort können Sie Zahlungsinformationen und weitere Abrechnungsdetails
              verwalten.
            </Typography>
          </SectionCard>

          <Dialog
            open={isSeatIncreaseDialogOpen}
            onClose={() => setSeatIncreaseDialogOpen(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Zusätzliche Sitze kostenpflichtig kaufen?</DialogTitle>

            <DialogContent>
              <Stack spacing={2} sx={{ pt: 1 }}>
                <Typography>
                  Sie erhöhen Ihr Abonnement von{" "}
                  <strong>{subscription.purchasedSeatQuantity}</strong> auf{" "}
                  <strong>{targetSeatQuantity}</strong> {seatWord(targetSeatQuantity)}.
                </Typography>

                <Alert severity="warning">
                  Dadurch kaufen Sie{" "}
                  <strong>
                    {absoluteSeatDelta} {seatWord(absoluteSeatDelta)}
                  </strong>{" "}
                  zusätzlich. Ihre monatlichen Kosten erhöhen sich um{" "}
                  <strong>
                    {formatCurrencyFromCents(monthlyDelta.netCents, subscription.currency)}
                  </strong>{" "}
                  netto zzgl.{" "}
                  <strong>{formatPercentFromBps(subscription.vatRateBps)} USt.</strong>
                </Alert>

                <Typography variant="body2" color="text.secondary">
                  Neue monatliche Kosten:{" "}
                  <strong>
                    {formatCurrencyFromCents(targetMonthlyPrice.netCents, subscription.currency)}
                  </strong>{" "}
                  netto zzgl.{" "}
                  {formatCurrencyFromCents(targetMonthlyPrice.vatCents, subscription.currency)}{" "}
                  USt. ={" "}
                  <strong>
                    {formatCurrencyFromCents(targetMonthlyPrice.grossCents, subscription.currency)}
                  </strong>{" "}
                  inkl. USt.
                </Typography>
              </Stack>
            </DialogContent>

            <DialogActions>
              <Button onClick={() => setSeatIncreaseDialogOpen(false)}>
                Abbrechen
              </Button>

              <Button
                variant="contained"
                onClick={confirmSeatIncrease}
                disabled={derived.isSeatChangeBusy}
              >
                {derived.isSeatChangeBusy ? "Wird gekauft …" : "Jetzt kostenpflichtig kaufen"}
              </Button>
            </DialogActions>
          </Dialog>
        </Stack>
      </PageContent>
    </>
  );
}
